import React, { useState, useEffect } from 'react';
import AssetSelectorModal from './AssetSelectorModal';
import { showToast } from '../context/ModalDialogContext';

const DEFAULT_TEMPLATES = {
  claim_verification: {
    name: 'Trophy Card Claim Verification',
    description: 'Dispatched when an angler or hunter links and claims a physical trophy coin or card into their personal digital vault.',
    subject: 'Claim your {{brand_name}} Trophy Card: {{card_title}}',
    headline: 'Claim Your Trophy Card',
    body: 'Someone has requested to link your trophy card \'{{card_title}}\' (Code: {{public_code}}) to a {{brand_name}} account.\n\nEnter your verification claim PIN below or click the button to claim your card directly into your vault.\n\nThis verification PIN will expire in 24 hours.',
    button_text: 'Claim Trophy Card',
    button_url: '{{claim_link}}',
    footer_text: 'Sent securely by {{brand_name}}. If you did not make this request, you can safely ignore this email.',
    banner_image_url: '',
    attachments: [],
    available_vars: [
      { tag: '{{brand_name}}', desc: 'Tenant brand name' },
      { tag: '{{card_title}}', desc: 'Trophy species or card title' },
      { tag: '{{public_code}}', desc: 'Public card identification code' },
      { tag: '{{claim_pin}}', desc: '6-digit verification PIN' },
      { tag: '{{claim_link}}', desc: 'Direct claim URL' },
      { tag: '{{tenant_domain}}', desc: 'Tenant web domain' }
    ]
  },
  card_allocated: {
    name: 'Official Trophy Card Certificate Notification',
    description: 'Dispatched when an official digital achievement certificate or trophy card is minted and allocated to an owner.',
    subject: 'Your Official Trophy Card Certificate is Ready: {{card_title}}',
    headline: 'Official Trophy Card Allocated',
    body: 'Congratulations! An official digital trophy card has been created and registered for {{recipient_name}}.\n\nSpecies / Title: {{card_title}}\nCoin / Serial Number: {{coin_number}}\n\nYou can view and showcase your verified trophy card anytime in the online registry.',
    button_text: 'View Trophy Card',
    button_url: 'https://{{tenant_domain}}/vault/{{coin_number}}',
    footer_text: 'Official registry maintained by {{brand_name}}.',
    banner_image_url: '',
    attachments: [],
    available_vars: [
      { tag: '{{brand_name}}', desc: 'Tenant brand name' },
      { tag: '{{recipient_name}}', desc: 'Owner or angler name' },
      { tag: '{{card_title}}', desc: 'Trophy card title or species' },
      { tag: '{{coin_number}}', desc: 'Physical coin serial number' },
      { tag: '{{tenant_domain}}', desc: 'Tenant web domain' }
    ]
  },
  order_confirmation: {
    name: 'Coin & Card Order Confirmation / Receipt',
    description: 'Dispatched immediately when an order for physical coins or registered certificates is received.',
    subject: 'Order Confirmation #{{order_id}} from {{brand_name}}',
    headline: 'Thank You for Your Order!',
    body: 'We have received your order #{{order_id}} for custom physical coins and registered digital certificates.\n\nOur minting team is currently preparing your order. You can track progress or review your order details using the link below.',
    button_text: 'View Order Status',
    button_url: 'https://{{tenant_domain}}/orders/{{order_id}}',
    footer_text: 'Questions about your order? Reply directly to this email or visit {{tenant_domain}}.',
    banner_image_url: '',
    attachments: [],
    available_vars: [
      { tag: '{{brand_name}}', desc: 'Tenant brand name' },
      { tag: '{{order_id}}', desc: 'Order tracking ID' },
      { tag: '{{recipient_name}}', desc: 'Customer name' },
      { tag: '{{total_amount}}', desc: 'Order total amount' },
      { tag: '{{tenant_domain}}', desc: 'Tenant web domain' }
    ]
  },
  operator_inquiry: {
    name: 'Portal Contact / Operator Inquiry',
    description: 'Dispatched to tenant administrators when an end user submits an inquiry or question via the portal.',
    subject: 'New Inquiry from {{sender_name}} regarding {{brand_name}}',
    headline: 'New Customer Inquiry Received',
    body: 'A new inquiry has been submitted through the {{brand_name}} portal.\n\nFrom: {{sender_name}} ({{sender_email}})\nPhone: {{sender_phone}}\n\nMessage:\n{{message_content}}',
    button_text: 'Open Operator Dashboard',
    button_url: 'https://{{tenant_domain}}/admin',
    footer_text: 'Automated notification from {{brand_name}} Operator Portal.',
    banner_image_url: '',
    attachments: [],
    available_vars: [
      { tag: '{{brand_name}}', desc: 'Tenant brand name' },
      { tag: '{{sender_name}}', desc: 'Sender full name' },
      { tag: '{{sender_email}}', desc: 'Sender email' },
      { tag: '{{sender_phone}}', desc: 'Sender phone' },
      { tag: '{{message_content}}', desc: 'Inquiry message text' },
      { tag: '{{tenant_domain}}', desc: 'Tenant web domain' }
    ]
  },
  welcome_invite: {
    name: 'Welcome / Account Invitation',
    description: 'Sent to welcome newly registered anglers, hunters, or club operators to the system.',
    subject: 'Welcome to {{brand_name}}',
    headline: 'Welcome to Your Trophy Vault',
    body: 'You have been invited to access the {{brand_name}} digital trophy system.\n\nManage your registered trophy coins, track your outdoor achievements, and showcase official certified cards from any mobile device or browser.',
    button_text: 'Get Started',
    button_url: 'https://{{tenant_domain}}/login',
    footer_text: 'Welcome to {{brand_name}}. Happy hunting & fishing!',
    banner_image_url: '',
    attachments: [],
    available_vars: [
      { tag: '{{brand_name}}', desc: 'Tenant brand name' },
      { tag: '{{recipient_name}}', desc: 'User full name' },
      { tag: '{{tenant_domain}}', desc: 'Tenant web domain' }
    ]
  },
  password_reset: {
    name: 'User Password Reset Notification',
    description: 'Dispatched when an administrator resets an account password, delivering secure credentials and sign-in instructions.',
    subject: 'Your {{brand_name}} Password Has Been Reset',
    headline: 'Password Reset Notification',
    body: 'Hello {{user_name}},\n\nYour account password for {{brand_name}} ({{user_email}}) has been securely reset by an administrator.\n\nPlease find your temporary secure password below. For your protection, we recommend changing your password upon logging in.',
    button_text: 'Sign In to Your Account',
    button_url: 'https://{{tenant_domain}}/login',
    footer_text: 'Sent securely by {{brand_name}}. If you did not request this change or have questions, please contact support at {{support_email}}.',
    banner_image_url: '',
    attachments: [],
    available_vars: [
      { tag: '{{brand_name}}', desc: 'Tenant brand name' },
      { tag: '{{user_name}}', desc: 'User full name or username' },
      { tag: '{{user_email}}', desc: 'User account email address' },
      { tag: '{{new_password}}', desc: 'Temporary secure generated password' },
      { tag: '{{tenant_domain}}', desc: 'Tenant web domain' },
      { tag: '{{support_email}}', desc: 'Support/help desk contact email' }
    ]
  }
};

const SAMPLE_VARS = {
  brand_name: "Fisherman's Tale",
  card_title: 'Largemouth Bass (8.4 lbs)',
  public_code: 'TC-8492-LMB',
  claim_pin: '782-194',
  claim_link: 'https://fishermanstale.com/?claim=782194',
  coin_number: '1004',
  recipient_name: 'Alex Mercer',
  recipient_email: 'alex.mercer@example.com',
  user_name: 'Alex Mercer',
  user_email: 'alex.mercer@example.com',
  new_password: 'k9#Pq$2mR!7xL@9vW&4bN*1',
  support_email: 'support@fishermanstale.com',
  order_id: 'ORD-5821',
  total_amount: '$149.00',
  sender_name: 'Sarah Miller',
  sender_email: 'sarah.m@example.com',
  sender_phone: '+1 (555) 234-5678',
  message_content: 'We are looking to order 50 custom minted coins for our upcoming tournament.',
  tenant_domain: 'fishermanstale.com'
};

export default function TenantMailSettingsTab({
  tenant,
  token,
  API_BASE = '',
  user,
  onSaveSuccess,
  isGlobalRole = false,
  selectedTenantId = null
}) {
  // Outbound Mail Provider & Credentials
  const [mailProvider, setMailProvider] = useState('default');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [resendApiKey, setResendApiKey] = useState('');
  const [showResendKey, setShowResendKey] = useState(false);

  // Sender Details
  const [fromName, setFromName] = useState('');
  const [noreplyEmail, setNoreplyEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');

  // Automated Message Templates Dictionary
  const [templates, setTemplates] = useState({});
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('claim_verification');

  // Preview Mode State
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile'

  // Asset Selector State
  const [isAssetSelectorOpen, setIsAssetSelectorOpen] = useState(false);
  const [assetSelectorMode, setAssetSelectorMode] = useState('banner'); // 'banner' | 'attachment'

  // Test Email Modal & Dispatch States
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testEmailError, setTestEmailError] = useState('');
  const [testSendMode, setTestSendMode] = useState('default_ping'); // 'default_ping' | 'current_template'

  // Save State
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // Initialize from tenant data
  useEffect(() => {
    if (tenant) {
      setMailProvider(tenant.mail_provider || 'default');
      setSmtpHost(tenant.smtp_host || '');
      setSmtpPort(tenant.smtp_port || 587);
      setSmtpUser(tenant.smtp_user || '');
      setSmtpPassword(tenant.smtp_password || '');
      setResendApiKey(tenant.resend_api_key || '');
      setFromName(tenant.from_name || '');
      setNoreplyEmail(tenant.noreply_email || '');
      setReplyToEmail(tenant.reply_to_email || '');
      setTemplates(tenant.email_templates || {});
    }
  }, [tenant]);

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (saveSuccess) showToast(saveSuccess, 'success');
  }, [saveSuccess]);

  useEffect(() => {
    if (saveError) showToast(saveError, 'error');
  }, [saveError]);

  useEffect(() => {
    if (testEmailError) showToast(testEmailError, 'error');
  }, [testEmailError]);

  useEffect(() => {
    if (testEmailResult) {
      if (testEmailResult.delivered) {
        showToast('Real test email successfully dispatched via SMTP!', 'success');
      } else if (testEmailResult.is_simulated) {
        showToast('Test email simulated (no SMTP relay configured).', 'warning');
      } else {
        showToast('Email delivery failed: ' + (testEmailResult.message || 'Error sending test email'), 'error');
      }
    }
  }, [testEmailResult]);

  // Current active template merged with defaults
  const currentDefault = DEFAULT_TEMPLATES[selectedTemplateKey] || DEFAULT_TEMPLATES.claim_verification;
  const currentTemplate = {
    ...currentDefault,
    ...(templates[selectedTemplateKey] || {})
  };

  const handleUpdateCurrentTemplate = (key, value) => {
    setTemplates(prev => ({
      ...prev,
      [selectedTemplateKey]: {
        ...(prev[selectedTemplateKey] || {}),
        [key]: value
      }
    }));
  };

  const handleResetTemplate = () => {
    if (window.confirm(`Reset "${currentDefault.name}" template to default settings?`)) {
      setTemplates(prev => {
        const next = { ...prev };
        delete next[selectedTemplateKey];
        return next;
      });
    }
  };

  // Asset selection handler
  const handleAssetSelect = (fullyQualifiedUrl, asset) => {
    if (assetSelectorMode === 'banner') {
      handleUpdateCurrentTemplate('banner_image_url', fullyQualifiedUrl);
    } else if (assetSelectorMode === 'attachment') {
      const newAtt = {
        name: asset.original_name || asset.filename,
        filename: asset.filename,
        url: fullyQualifiedUrl,
        size: asset.file_size || 0,
        mime_type: asset.mime_type || ''
      };
      const currentAtts = currentTemplate.attachments || [];
      handleUpdateCurrentTemplate('attachments', [...currentAtts, newAtt]);
    }
    setIsAssetSelectorOpen(false);
  };

  const handleRemoveAttachment = (index) => {
    const currentAtts = currentTemplate.attachments || [];
    handleUpdateCurrentTemplate('attachments', currentAtts.filter((_, i) => i !== index));
  };

  // Variable Interpolation for Live Preview
  const interpolate = (text) => {
    if (!text) return '';
    const mergedVars = {
      ...SAMPLE_VARS,
      brand_name: tenant?.brand_name || SAMPLE_VARS.brand_name,
      tenant_domain: tenant?.domain_name || SAMPLE_VARS.tenant_domain
    };
    let result = text;
    for (const [k, v] of Object.entries(mergedVars)) {
      result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
    }
    return result;
  };

  // Save Mail Settings
  const handleSaveAll = async () => {
    setSaving(true);
    setSaveSuccess('');
    setSaveError('');

    const targetId = tenant?.id || selectedTenantId;
    if (!targetId) {
      setSaveError('No tenant selected.');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        mail_provider: mailProvider,
        smtp_host: smtpHost.trim() || null,
        smtp_port: parseInt(smtpPort, 10) || 587,
        smtp_user: smtpUser.trim() || null,
        smtp_password: smtpPassword || null,
        resend_api_key: resendApiKey.trim() || null,
        from_name: fromName.trim() || null,
        noreply_email: noreplyEmail.trim() || null,
        reply_to_email: replyToEmail.trim() || null,
        email_templates: templates
      };

      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${targetId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save mail configuration.');
      }

      setSaveSuccess('Mail and automated messaging settings successfully saved!');
      if (onSaveSuccess) onSaveSuccess(data);
      setTimeout(() => setSaveSuccess(''), 5000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Dispatch Test Email
  const handleSendTestEmail = async () => {
    setTestEmailLoading(true);
    setTestEmailError('');
    setTestEmailResult(null);

    const targetId = tenant?.id || selectedTenantId;
    if (!targetId) {
      setTestEmailError('Unable to identify tenant ID for testing.');
      setTestEmailLoading(false);
      return;
    }

    try {
      const payload = {
        recipient_email: (testRecipient || user?.email || '').trim(),
        noreply_email: noreplyEmail.trim() || null,
        from_name: fromName.trim() || null,
        mail_provider: mailProvider,
        smtp_host: smtpHost.trim() || null,
        smtp_port: parseInt(smtpPort, 10) || 587,
        smtp_user: smtpUser.trim() || null,
        smtp_password: smtpPassword || null,
        resend_api_key: resendApiKey.trim() || null
      };

      if (testSendMode === 'current_template') {
        payload.template_key = selectedTemplateKey;
        payload.custom_subject = currentTemplate.subject;
        payload.custom_headline = currentTemplate.headline;
        payload.custom_body = currentTemplate.body;
        payload.banner_image_url = currentTemplate.banner_image_url || null;
        payload.attachments = currentTemplate.attachments || [];
      }

      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${targetId}/test-noreply-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to dispatch test verification email.');
      }
      setTestEmailResult(data);
    } catch (err) {
      setTestEmailError(err.message);
    } finally {
      setTestEmailLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
      
      {/* Top Banner & Quick Actions */}
      <div style={{
        background: 'var(--card-bg, #1e293b)',
        border: '1px solid var(--border-color, #334155)',
        borderRadius: '12px',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            backgroundColor: 'rgba(56, 139, 253, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px'
          }}>
            📬
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main, #f8fafc)' }}>
              Mail &amp; Automated Messaging
            </h3>
            <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: 'var(--text-mute, #94a3b8)' }}>
              Configure tenant email relays, sender addresses, and customize automated notifications with live visual mockups.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="test-noreply-email-btn"
            onClick={() => {
              setTestRecipient(user?.email || '');
              setTestEmailResult(null);
              setTestEmailError('');
              setIsTestModalOpen(true);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'var(--panel-color, rgba(255, 255, 255, 0.06))',
              border: '1px solid var(--border-color, #475569)',
              color: 'var(--text-main, #f8fafc)',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            Send Test Verification Email
          </button>

          <button
            type="button"
            onClick={handleSaveAll}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'var(--primary-color, #2563eb)',
              color: '#ffffff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              opacity: saving ? 0.7 : 1,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            {saving ? 'Saving Settings...' : 'Save Mail Settings'}
          </button>
        </div>
      </div>

      {/* Success / Error Alerts */}
      {saveSuccess && (
        <div style={{
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          border: '1px solid rgba(34, 197, 94, 0.4)',
          borderRadius: '8px',
          color: '#4ade80',
          padding: '12px 18px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>✅</span>
          <span>{saveSuccess}</span>
        </div>
      )}
      {saveError && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: '8px',
          color: '#f87171',
          padding: '12px 18px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>❌</span>
          <span>{saveError}</span>
        </div>
      )}

      {/* Grid: Settings on Left, Template Editor & Mockup on Right */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(320px, 420px) minmax(400px, 1fr)',
        gap: '24px',
        alignItems: 'start'
      }}>

        {/* Column 1: Outbound Mail Delivery & Sender Configuration */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Provider Card */}
          <div style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main, #f8fafc)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🚀</span> Outbound Mail Provider
              </h4>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #94a3b8)' }}>
                Select how your emails are routed and delivered to recipients.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
              {[
                { id: 'default', title: 'System Default Relay', desc: 'Use TrophyCoin platform SMTP or Resend relay' },
                { id: 'smtp', title: 'Custom SMTP Server', desc: 'Direct connection to your company or club mail host' },
                { id: 'resend', title: 'Resend REST API', desc: 'Modern high-deliverability HTTP API via api.resend.com' }
              ].map(opt => (
                <label
                  key={opt.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: mailProvider === opt.id ? '1px solid var(--primary-color, #2563eb)' : '1px solid var(--border-color, #334155)',
                    backgroundColor: mailProvider === opt.id ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <input
                    type="radio"
                    name="mail_provider_radio"
                    value={opt.id}
                    checked={mailProvider === opt.id}
                    onChange={(e) => setMailProvider(e.target.value)}
                    style={{ marginTop: '3px' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                      {opt.title}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)', marginTop: '2px' }}>
                      {opt.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {/* Custom SMTP Config */}
            {mailProvider === 'smtp' && (
              <div style={{
                borderTop: '1px solid var(--border-color, #334155)',
                paddingTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 3 }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. smtp.mailgun.org"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: 'var(--input-bg, #0f172a)',
                        border: '1px solid var(--input-border, #334155)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--input-text, #f8fafc)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                      Port
                    </label>
                    <input
                      type="number"
                      placeholder="587"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: 'var(--input-bg, #0f172a)',
                        border: '1px solid var(--input-border, #334155)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--input-text, #f8fafc)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                    SMTP Username / Login
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. postmaster@yourdomain.com"
                    value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--input-bg, #0f172a)',
                      border: '1px solid var(--input-border, #334155)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text, #f8fafc)',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                      SMTP Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-mute, #94a3b8)', fontSize: '11px', cursor: 'pointer' }}
                    >
                      {showSmtpPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <input
                    type={showSmtpPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--input-bg, #0f172a)',
                      border: '1px solid var(--input-border, #334155)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text, #f8fafc)',
                      fontSize: '13px',
                      fontFamily: showSmtpPassword ? 'inherit' : 'monospace'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Resend API Config */}
            {mailProvider === 'resend' && (
              <div style={{
                borderTop: '1px solid var(--border-color, #334155)',
                paddingTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                    Resend API Key
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowResendKey(!showResendKey)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-mute, #94a3b8)', fontSize: '11px', cursor: 'pointer' }}
                  >
                    {showResendKey ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input
                  type={showResendKey ? 'text' : 'password'}
                  placeholder="re_123456789..."
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--input-border, #334155)',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    color: 'var(--input-text, #f8fafc)',
                    fontSize: '13px',
                    fontFamily: showResendKey ? 'inherit' : 'monospace'
                  }}
                />
                <span style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)' }}>
                  Create or manage keys at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color, #2563eb)' }}>resend.com/api-keys</a>.
                </span>
              </div>
            )}
          </div>

          {/* Sender Identity Card */}
          <div style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main, #f8fafc)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>👤</span> Sender Identity &amp; Addresses
              </h4>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #94a3b8)' }}>
                Customize how your tenant brand is displayed in the recipient inbox.
              </p>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                From Display Name
              </label>
              <input
                type="text"
                placeholder={tenant?.brand_name || "e.g. Fisherman's Tale Official"}
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--input-bg, #0f172a)',
                  border: '1px solid var(--input-border, #334155)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: 'var(--input-text, #f8fafc)',
                  fontSize: '13px'
                }}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)', marginTop: '2px', display: 'block' }}>
                Defaults to your tenant brand name ({tenant?.brand_name || 'TrophyCoin'}).
              </span>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                No-Reply / Sender Email Address
              </label>
              <input
                type="email"
                data-testid="noreply-email-input"
                placeholder={`no-reply@${tenant?.domain_name || 'tenantdomain.com'}`}
                value={noreplyEmail}
                onChange={(e) => setNoreplyEmail(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--input-bg, #0f172a)',
                  border: '1px solid var(--input-border, #334155)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: 'var(--input-text, #f8fafc)',
                  fontSize: '13px'
                }}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)', marginTop: '2px', display: 'block' }}>
                Default: <code>no-reply@{tenant?.domain_name || 'tenantdomain.com'}</code>.
              </span>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                Reply-To Email Address (Optional)
              </label>
              <input
                type="email"
                placeholder={`support@${tenant?.domain_name || 'tenantdomain.com'}`}
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--input-bg, #0f172a)',
                  border: '1px solid var(--input-border, #334155)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: 'var(--input-text, #f8fafc)',
                  fontSize: '13px'
                }}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)', marginTop: '2px', display: 'block' }}>
                Where user replies are routed when they click &quot;Reply&quot; in their mail client.
              </span>
            </div>
          </div>
        </div>

        {/* Column 2: Automated Messages Dropdown & Visual Mockup */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Template Selection Dropdown Header */}
          <div style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color, #2563eb)' }}>
                  Automated Message Template
                </label>
                <div style={{ fontSize: '13px', color: 'var(--text-mute, #94a3b8)', marginTop: '2px' }}>
                  Select an automated system message to adjust content, layout, and attached assets.
                </div>
              </div>

              <button
                type="button"
                onClick={handleResetTemplate}
                style={{
                  background: 'none',
                  border: '1px solid var(--border-color, #334155)',
                  color: 'var(--text-mute, #94a3b8)',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Reset to Default
              </button>
            </div>

            <select
              value={selectedTemplateKey}
              onChange={(e) => setSelectedTemplateKey(e.target.value)}
              style={{
                width: '100%',
                backgroundColor: 'var(--input-bg, #0f172a)',
                border: '1px solid var(--input-border, #334155)',
                borderRadius: '8px',
                padding: '10px 14px',
                color: 'var(--input-text, #f8fafc)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {Object.entries(DEFAULT_TEMPLATES).map(([key, tpl]) => (
                <option key={key} value={key}>
                  {tpl.name}
                </option>
              ))}
            </select>

            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '12px',
              color: 'var(--text-mute, #94a3b8)',
              borderLeft: '3px solid var(--primary-color, #2563eb)'
            }}>
              {currentDefault.description}
            </div>

            {/* Dynamic Variables Chips */}
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute, #94a3b8)', marginBottom: '6px' }}>
                AVAILABLE DYNAMIC TAGS (click to insert):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(currentDefault.available_vars || []).map(v => (
                  <button
                    key={v.tag}
                    type="button"
                    title={v.desc}
                    onClick={() => {
                      handleUpdateCurrentTemplate('body', (currentTemplate.body || '') + ' ' + v.tag);
                    }}
                    style={{
                      backgroundColor: 'rgba(56, 139, 253, 0.1)',
                      border: '1px solid rgba(56, 139, 253, 0.3)',
                      color: 'var(--primary-color, #58a6ff)',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontFamily: 'monospace',
                      cursor: 'pointer'
                    }}
                  >
                    {v.tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Template Fields & Interactive Mockup Tabs */}
          <div style={{
            background: 'var(--card-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            {/* Split Controls: Content Editor Form and Live Preview */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--border-color, #334155)',
              padding: '12px 20px',
              backgroundColor: 'rgba(255, 255, 255, 0.02)'
            }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main, #f8fafc)' }}>
                Template Content &amp; Media Assets
              </span>

              {/* Desktop / Mobile Mockup Toggle */}
              <div style={{ display: 'inline-flex', backgroundColor: 'var(--panel-color, #0f172a)', borderRadius: '6px', padding: '2px', border: '1px solid var(--border-color, #334155)' }}>
                <button
                  type="button"
                  onClick={() => setPreviewDevice('desktop')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: previewDevice === 'desktop' ? 'var(--primary-color, #2563eb)' : 'transparent',
                    color: previewDevice === 'desktop' ? '#ffffff' : 'var(--text-mute, #94a3b8)',
                    cursor: 'pointer'
                  }}
                >
                  🖥️ Desktop View
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDevice('mobile')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: previewDevice === 'mobile' ? 'var(--primary-color, #2563eb)' : 'transparent',
                    color: previewDevice === 'mobile' ? '#ffffff' : 'var(--text-mute, #94a3b8)',
                    cursor: 'pointer'
                  }}
                >
                  📱 Mobile View
                </button>
              </div>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Subject & Headline */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                    Email Subject Line
                  </label>
                  <input
                    type="text"
                    value={currentTemplate.subject || ''}
                    onChange={(e) => handleUpdateCurrentTemplate('subject', e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--input-bg, #0f172a)',
                      border: '1px solid var(--input-border, #334155)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text, #f8fafc)',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                    Email Header Headline
                  </label>
                  <input
                    type="text"
                    value={currentTemplate.headline || ''}
                    onChange={(e) => handleUpdateCurrentTemplate('headline', e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--input-bg, #0f172a)',
                      border: '1px solid var(--input-border, #334155)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text, #f8fafc)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Body Text */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                  Message Body (supports paragraphs &amp; dynamic variables)
                </label>
                <textarea
                  rows={5}
                  value={currentTemplate.body || ''}
                  onChange={(e) => handleUpdateCurrentTemplate('body', e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--input-border, #334155)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: 'var(--input-text, #f8fafc)',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Call-to-Action Button */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                    CTA Button Label
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Claim Trophy Card"
                    value={currentTemplate.button_text || ''}
                    onChange={(e) => handleUpdateCurrentTemplate('button_text', e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--input-bg, #0f172a)',
                      border: '1px solid var(--input-border, #334155)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text, #f8fafc)',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                    CTA Button URL Link
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. {{claim_link}}"
                    value={currentTemplate.button_url || ''}
                    onChange={(e) => handleUpdateCurrentTemplate('button_url', e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--input-bg, #0f172a)',
                      border: '1px solid var(--input-border, #334155)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text, #f8fafc)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Media & Attachments from Assets Library */}
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid var(--border-color, #334155)',
                borderRadius: '8px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main, #f8fafc)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🖼️</span> Assets Library Integration (Images &amp; Attachments)
                </div>

                {/* Banner Image Asset Selector */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {currentTemplate.banner_image_url ? (
                      <img
                        src={currentTemplate.banner_image_url}
                        alt="Header Banner Preview"
                        style={{ width: '80px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-color, #334155)' }}
                      />
                    ) : (
                      <div style={{
                        width: '80px',
                        height: '40px',
                        borderRadius: '4px',
                        backgroundColor: 'var(--panel-color, #0f172a)',
                        border: '1px dashed var(--border-color, #334155)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        color: 'var(--text-mute, #94a3b8)'
                      }}>
                        No Banner
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                        Header Banner Image
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)' }}>
                        Displayed at top of email. Select from Asset Library.
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setAssetSelectorMode('banner');
                        setIsAssetSelectorOpen(true);
                      }}
                      style={{
                        backgroundColor: 'var(--primary-color, #2563eb)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Choose Banner from Assets
                    </button>
                    {currentTemplate.banner_image_url && (
                      <button
                        type="button"
                        onClick={() => handleUpdateCurrentTemplate('banner_image_url', '')}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#f87171',
                          border: '1px solid rgba(248, 113, 113, 0.4)',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Attachments Section */}
                <div style={{ borderTop: '1px solid var(--border-color, #334155)', paddingTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                      Email Attachments (PDFs, Certificate Documents, etc.)
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAssetSelectorMode('attachment');
                        setIsAssetSelectorOpen(true);
                      }}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        color: 'var(--text-main, #f8fafc)',
                        border: '1px solid var(--border-color, #334155)',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      + Add File from Assets
                    </button>
                  </div>

                  {(!currentTemplate.attachments || currentTemplate.attachments.length === 0) ? (
                    <div style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)', fontStyle: 'italic' }}>
                      No attachments configured for this email template.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {currentTemplate.attachments.map((att, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: 'var(--input-bg, #0f172a)',
                            border: '1px solid var(--border-color, #334155)',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>📎</span>
                            <span style={{ fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                              {att.name || att.filename}
                            </span>
                            {att.size > 0 && (
                              <span style={{ color: 'var(--text-mute, #94a3b8)', fontSize: '11px' }}>
                                ({(att.size / 1024).toFixed(0)} KB)
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(idx)}
                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '12px' }}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Note */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '4px' }}>
                  Footer Text &amp; Disclaimer
                </label>
                <input
                  type="text"
                  value={currentTemplate.footer_text || ''}
                  onChange={(e) => handleUpdateCurrentTemplate('footer_text', e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--input-border, #334155)',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    color: 'var(--input-text, #f8fafc)',
                    fontSize: '13px'
                  }}
                />
              </div>

              {/* Interactive Live Email Client Mockup */}
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-mute, #94a3b8)', marginBottom: '8px' }}>
                  Live Client Mockup Preview
                </div>

                {/* Simulated Email Client Container */}
                <div style={{
                  backgroundColor: '#0f172a',
                  border: '1px solid var(--border-color, #334155)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  overflow: 'hidden',
                  maxWidth: previewDevice === 'mobile' ? '380px' : '100%',
                  margin: previewDevice === 'mobile' ? '0 auto' : '0'
                }}>
                  
                  {/* Window Chrome Header Bar */}
                  <div style={{
                    backgroundColor: '#1e293b',
                    padding: '8px 14px',
                    borderBottom: '1px solid #334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#eab308' }} />
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#22c55e' }} />
                    <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px', fontWeight: '600' }}>
                      Inbox Preview &bull; {interpolate(currentTemplate.subject)}
                    </span>
                  </div>

                  {/* Mail Message Header Bar */}
                  <div style={{
                    backgroundColor: '#182234',
                    padding: '12px 16px',
                    borderBottom: '1px solid #334155',
                    fontSize: '12px',
                    color: '#94a3b8',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div>
                      <strong style={{ color: '#f8fafc' }}>Subject:</strong> {interpolate(currentTemplate.subject)}
                    </div>
                    <div>
                      <strong style={{ color: '#f8fafc' }}>From:</strong> {fromName || tenant?.brand_name || 'TrophyCoin'} &lt;{noreplyEmail || `no-reply@${tenant?.domain_name || 'trophycoin.io'}`}&gt;
                    </div>
                    <div>
                      <strong style={{ color: '#f8fafc' }}>To:</strong> Alex Mercer &lt;alex@example.com&gt;
                    </div>
                    {replyToEmail && (
                      <div>
                        <strong style={{ color: '#f8fafc' }}>Reply-To:</strong> {replyToEmail}
                      </div>
                    )}
                  </div>

                  {/* Email Body Canvas */}
                  <div style={{
                    backgroundColor: '#f1f5f9',
                    padding: previewDevice === 'mobile' ? '16px 12px' : '24px 20px',
                    color: '#0f172a'
                  }}>
                    <div style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      padding: '24px'
                    }}>
                      
                      {/* Optional Header Banner */}
                      {currentTemplate.banner_image_url && (
                        <div style={{ margin: '-24px -24px 20px -24px', maxHeight: '160px', overflow: 'hidden' }}>
                          <img
                            src={currentTemplate.banner_image_url}
                            alt="Header Banner"
                            style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', display: 'block' }}
                          />
                        </div>
                      )}

                      {/* Brand Logo / Brand Name */}
                      <div style={{ marginBottom: '16px' }}>
                        {tenant?.logo_url ? (
                          <img
                            src={tenant.logo_url}
                            alt={tenant.brand_name || 'Brand Logo'}
                            style={{ maxHeight: '40px', maxWidth: '160px', objectFit: 'contain' }}
                          />
                        ) : (
                          <span style={{ fontSize: '18px', fontWeight: '800', color: tenant?.primary_color || '#2563eb' }}>
                            {tenant?.brand_name || "Fisherman's Tale"}
                          </span>
                        )}
                      </div>

                      {/* Headline */}
                      <h2 style={{
                        margin: '0 0 16px 0',
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#0f172a',
                        borderBottom: '2px solid #f1f5f9',
                        paddingBottom: '10px'
                      }}>
                        {interpolate(currentTemplate.headline)}
                      </h2>

                      {/* Body Paragraphs */}
                      <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#334155' }}>
                        {interpolate(currentTemplate.body).split('\n\n').map((paragraph, pIdx) => (
                          <p key={pIdx} style={{ margin: '0 0 12px 0', whiteSpace: 'pre-line' }}>
                            {paragraph}
                          </p>
                        ))}
                      </div>

                      {/* Highlighted PIN for Claim Verification */}
                      {selectedTemplateKey === 'claim_verification' && (
                        <div style={{
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '14px',
                          textAlign: 'center',
                          margin: '18px 0'
                        }}>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', fontWeight: '700', marginBottom: '4px' }}>
                            Sample Claim PIN
                          </div>
                          <div style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '2px', color: '#0f172a', fontFamily: 'monospace' }}>
                            782-194
                          </div>
                        </div>
                      )}

                      {/* Highlighted Password for Password Reset */}
                      {selectedTemplateKey === 'password_reset' && (
                        <div style={{
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '14px',
                          textAlign: 'center',
                          margin: '18px 0'
                        }}>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', fontWeight: '700', marginBottom: '4px' }}>
                            Temporary Secure Password
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: '700', letterSpacing: '1px', color: '#0f172a', fontFamily: 'monospace', padding: '6px 14px', background: '#ffffff', border: '1px dashed #cbd5e1', borderRadius: '6px', display: 'inline-block' }}>
                            k9#Pq$2mR!7xL@9vW&4bN*1
                          </div>
                        </div>
                      )}

                      {/* Call-to-Action Button */}
                      {currentTemplate.button_text && (
                        <div style={{ textAlign: 'center', margin: '20px 0 14px 0' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              backgroundColor: tenant?.primary_color || '#2563eb',
                              color: '#ffffff',
                              padding: '11px 24px',
                              borderRadius: '6px',
                              fontWeight: '700',
                              fontSize: '14px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {interpolate(currentTemplate.button_text)}
                          </span>
                        </div>
                      )}

                      {/* Attached Documents Notice in Mockup */}
                      {currentTemplate.attachments && currentTemplate.attachments.length > 0 && (
                        <div style={{
                          backgroundColor: '#f1f5f9',
                          borderRadius: '6px',
                          padding: '10px 14px',
                          margin: '16px 0 0 0',
                          fontSize: '12px',
                          color: '#475569'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            📎 Attached Documents ({currentTemplate.attachments.length}):
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {currentTemplate.attachments.map((att, i) => (
                              <div key={i}>
                                &bull; {att.name || att.filename}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer Note */}
                      <div style={{
                        borderTop: '1px solid #e2e8f0',
                        marginTop: '20px',
                        paddingTop: '14px',
                        fontSize: '11px',
                        color: '#94a3b8',
                        textAlign: 'center',
                        lineHeight: '1.4'
                      }}>
                        {interpolate(currentTemplate.footer_text)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Asset Selector Modal */}
      {isAssetSelectorOpen && (
        <AssetSelectorModal
          isOpen={isAssetSelectorOpen}
          onClose={() => setIsAssetSelectorOpen(false)}
          onSelect={handleAssetSelect}
          title={assetSelectorMode === 'banner' ? 'Select Email Banner Image' : 'Select Email Attachment File'}
          tenantId={tenant?.id || selectedTenantId}
          user={user}
          assetType={assetSelectorMode === 'banner' ? 'image' : 'all'}
        />
      )}

      {/* Test Email Modal Dialog */}
      {isTestModalOpen && (
        <div data-testid="test-email-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '16px',
          boxSizing: 'border-box',
          backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            background: 'var(--panel-bg, #1e293b)',
            border: '1px solid var(--border-color, #334155)',
            borderRadius: '14px',
            maxWidth: '540px',
            width: '100%',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #334155)', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '22px' }}>✉️</span>
                <div>
                  <h3 style={{ margin: 0, color: 'var(--primary-color, #2563eb)', fontSize: '17px', fontWeight: '700' }}>
                    Test No-Reply Email Configuration
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #94a3b8)' }}>
                    Verify outbound mail delivery, sender credentials, or template formatting.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsTestModalOpen(false);
                  setTestEmailError('');
                  setTestEmailResult(null);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--text-mute, #94a3b8)', fontSize: '20px', cursor: 'pointer', padding: '4px' }}
              >
                ✕
              </button>
            </div>

            {/* Error Message */}
            {testEmailError && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '6px',
                color: '#f87171',
                padding: '10px 14px',
                fontSize: '13px'
              }}>
                {testEmailError}
              </div>
            )}

            {/* Success / Status Message */}
            {testEmailResult && (
              <div style={{
                backgroundColor: testEmailResult.delivered
                  ? 'rgba(34, 197, 94, 0.15)'
                  : (testEmailResult.is_simulated ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)'),
                border: `1px solid ${testEmailResult.delivered ? 'rgba(34, 197, 94, 0.4)' : (testEmailResult.is_simulated ? 'rgba(234, 179, 8, 0.4)' : 'rgba(239, 68, 68, 0.4)')}`,
                borderRadius: '6px',
                color: testEmailResult.delivered ? '#4ade80' : (testEmailResult.is_simulated ? '#facc15' : '#f87171'),
                padding: '12px 14px',
                fontSize: '13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                  <span>{testEmailResult.delivered ? '✅ Real Email Dispatched via SMTP!' : (testEmailResult.is_simulated ? '⚠️ Simulation Mode (No SMTP Relay Configured)' : '❌ Email Delivery Failed')}</span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-main, #f8fafc)', opacity: 0.9 }}>
                  <div><strong>From:</strong> {testEmailResult.sender_email}</div>
                  <div><strong>To:</strong> {testEmailResult.recipient_email}</div>
                  <div><strong>Sample Verification PIN:</strong> <code style={{ backgroundColor: 'rgba(0,0,0,0.25)', padding: '2px 6px', borderRadius: '4px' }}>{testEmailResult.sample_token}</code></div>
                  <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-mute, #94a3b8)', borderTop: '1px solid var(--border-color, #334155)', paddingTop: '6px' }}>
                    {testEmailResult.message}
                  </div>
                </div>
              </div>
            )}

            {/* Test Mode Selector */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block', marginBottom: '6px' }}>
                Test Message Type
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #334155)',
                  backgroundColor: testSendMode === 'default_ping' ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="test_send_mode"
                    value="default_ping"
                    checked={testSendMode === 'default_ping'}
                    onChange={(e) => setTestSendMode(e.target.value)}
                  />
                  <span>Verification PIN Ping</span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #334155)',
                  backgroundColor: testSendMode === 'current_template' ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="test_send_mode"
                    value="current_template"
                    checked={testSendMode === 'current_template'}
                    onChange={(e) => setTestSendMode(e.target.value)}
                  />
                  <span>Current Template ({currentDefault.name.split(' ')[0]})</span>
                </label>
              </div>
            </div>

            {/* Recipient Input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main, #f8fafc)' }}>
                Recipient Email Address (To)
              </label>
              <input
                type="email"
                required
                data-testid="test-email-recipient-input"
                placeholder="youremail@example.com"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                style={{
                  backgroundColor: 'var(--input-bg, #0f172a)',
                  border: '1px solid var(--input-border, #334155)',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  color: 'var(--input-text, #f8fafc)',
                  fontSize: '14px'
                }}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)' }}>
                Defaults to your logged-in administrator email.
              </span>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid var(--border-color, #334155)', paddingTop: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  setIsTestModalOpen(false);
                  setTestEmailError('');
                  setTestEmailResult(null);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border-color, #334155)',
                  color: 'var(--text-mute, #94a3b8)',
                  borderRadius: '6px',
                  padding: '9px 16px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                type="button"
                data-testid="confirm-send-test-email-btn"
                disabled={testEmailLoading || !testRecipient.trim()}
                onClick={handleSendTestEmail}
                style={{
                  backgroundColor: 'var(--primary-color, #2563eb)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '9px 18px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: testEmailLoading || !testRecipient.trim() ? 0.6 : 1
                }}
              >
                {testEmailLoading ? 'Sending Test...' : 'Send Test Verification Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
